export function uniqueifyName(otherNames: string[], currentName: string): string {
  // if not already used, no problem
  if (!otherNames.some(n => n === currentName)) {
    return currentName;
  }

  // regex for checking if string have trailing number
  // We should group base text and number separately
  // (This is a bit more complicated than it needs to be, because we want to support names like "Foo (2)" as well as "Foo 2"
  let [, baseText, numberText] = currentName.match(/^(.+?)\s*(?:\(|\s)\s*(\d+)\)?$/) || [, null, null];
  let number = parseInt(numberText);
  if (!baseText || !isFinite(number)) {
    baseText = currentName.trim();
    number = 1;
  }
  // remove eventual "Copy" from the end of text
  baseText = baseText.replace(/\s*Copy$/, '');

  // find the maximum such trailing number
  let baseLen = baseText.length;
  for (let other of otherNames) {
    if (!other.startsWith(baseText)) {
      continue;
    }
    other = other.slice(baseLen);
    let [, t] = other.match(/(\d+)/) || [, null];
    let n = parseInt(t);
    if (isFinite(n) && n > number) {
      number = n;
    }
  }

  // return the next higher
  return `${baseText} ${number + 1}`;
}

export function generateIdFromName(name: string, joiner: string = '-') {
  // autogenerate id from name by...
  return (
    name
      // setting all characters lowercase
      .toLowerCase()
      // replacing everything but alphanum with spaces
      .replace(/[^a-zA-Z0-9]/g, ' ')
      // splitting along spaces
      .split(' ')
      // removing excessive duplicate spaces
      .map(s => s.trim())
      // removing all substrings that contained only spaces
      .filter(s => s.length > 0)
      // and joining it all back with underscores
      .join(joiner)
  );
}

export function splitTextIntoSentences(text: string): string[] {
  // Define common abbreviations to avoid splitting
  const abbreviations = ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.', 'E.g.', 'E.g', 'e.g', 'e.g.',];

  // Use a regular expression to split the text into sentences
  // Split text into sentences, including the punctuation marks
  const sentences = text.split(/([.!?])\s+/).reduce((acc, sentence, index, array) => {
    if (index % 2 === 0) {
      // Add the next element (punctuation) to the current sentence
      acc.push(sentence + (array[index + 1] || ''));
    }
    return acc;
  }, []);

  // Merge sentences that are abbreviations or initials
  const mergedSentences: string[] = [];
  let currentSentence = '';

  sentences.forEach(sentence => {
    currentSentence += sentence.trim();

    // Check if the current sentence ends with an abbreviation
    const endsWithAbbreviation = abbreviations.some(abbreviation => currentSentence.endsWith(abbreviation));

    if (!endsWithAbbreviation) {
      mergedSentences.push(currentSentence);
      currentSentence = '';
    } else {
      currentSentence += ' ';
    }
  });

  // Add any remaining sentence
  if (currentSentence.trim() !== '') {
    mergedSentences.push(currentSentence);
  }

  return mergedSentences;
}

export function htmlToText(htmlString) {
  const entityMap = {
    "&lt;": "<",
    "&gt;": ">",
    "&amp;": "&",
    "&quot;": "\"",
    "&#39;": "'"
    // Add more entities here as needed
  };

  return htmlString
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&[#\w]+;/g, match => entityMap[match] || match); // Replace entities
}

/// Split a full name into first and last name.
export function splitFirstLastName(fullName: string): { firstName: string, lastName: string } {
  if (!fullName) return { firstName: '', lastName: '' };

  const parts = fullName.trim().split(/\s+/); // split on one or more spaces

  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  } else if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  } else {
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ');
    return { firstName: firstName, lastName: lastName };
  }
}
